import { History } from 'history';
import React, { RefObject } from 'react';

import { type Video, VideoFields } from '@vimeo/gctl-api/models/components/schemas/video/Video';
import { humanize, translate } from '@vimeo/i18n-runtime';

import {
  ALIGNMENT_VALUES,
  FALLBACK_THUMBNAIL_URL,
  FALLBACK_THUMBNAIL_URL_2X,
  SHOWCASE_BASE_URL,
} from './constants';
import { Clip, GridSize, VideoDetailsDisplayConfigOptions } from './types';

// Prevent custom click for modifier keys or middle mouse (lets browser handle new tab/window)
export const isModifierOrMiddleClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) =>
  e.button === 1 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey;

// Get the clip ID from the clip URI
export const getClipIdFromUri = (clipUri: string) => {
  if (!clipUri) return undefined;
  return clipUri?.split?.('/videos/')?.[1]?.split(':')?.[0];
};

// Get the clip hash from the clip URI
export const getClipHashFromUri = (clipUri: string) => {
  if (!clipUri) return undefined;
  return clipUri?.split?.('/videos/')?.[1]?.split(':')?.[1];
};

/**
 * Returns the search params from the current URL
 */
export const getSearchParams = () => {
  if (typeof window === 'undefined') {
    return {
      get: () => {
        console.log('window undefined');
        return '';
      },
    };
  }
  return new URLSearchParams(window.location.search);
};

/**
 * Parse video id from the url to "?video=:id"
 * @param options.eventId - Event id to be appended to the query to override it as event
 * @param options.baseUrl - Base url to be used to override the link base url
 * This should be used only for GCTL data `link`:
 * - In pages, there might be other search query from the page level
 * - In GCTL uri, the clipHash is append with `:` not `/`
 * @example contextUrlParser('/videos/1234', '/videos/1234') // returns { clipId: '1234', clipRequestId: '1234', href: '/manage/showcases/6789?video=1234' }
 * @example contextUrlParser('/videos/1234/ab12', '/videos/1234/ab12') // returns { clipId: '1234', clipRequestId: '1234:ab12', href: '/manage/showcases/6789?video=1234' }
 * @example contextUrlParser('/videos/1234/ab12', '/videos/1234/ab12', { eventId: '1023' }) // returns { clipId: '1234', clipRequestId: '1234:ab12', href: '/manage/showcases/6789?event=1023&video=1234' }
 */
export const contextUrlParser = (
  url: string,
  videoLink: string,
  options?: { eventId?: string; baseUrl?: string },
) => {
  const baseUrl = options?.baseUrl || '';

  const clipId = url?.split?.('/videos/')?.[1] || '';
  const match = videoLink.match(/(?:.*\/)(\d+(?::\d+)?)(?:\/?|$)/);
  const clipRequestId = match?.[2] ? `${clipId}:${match?.[2]}` : clipId;

  const query = options?.eventId ? `event=${options.eventId}&video=${clipId}` : `video=${clipId}`;
  const link = clipId ? `${baseUrl}?${query}` : baseUrl;

  return { clipId, clipRequestId, link };
};

/**
 * Get the event id from the clip object if status is not 'streaming_error' or 'done'
 * @example
 * getEventIdFromClip({ live: { status: 'streaming', recurringEvent: { link: '/events/1234' } } }) //=> '1234'
 */
export const getEventIdFromClip = (clip: Video) => getEventIdFromClipLive(clip?.live);

export const getEventIdFromClipLive = (live: Video['live']) =>
  !['streaming_error', 'done'].includes(live?.status)
    ? live?.recurringEvent?.link?.split?.('/')?.pop()
    : undefined;

const matureContent = ['language', 'drugs', 'nudity', 'violence'];
const isMature = (rating: string) => matureContent.includes(rating);

export const clipBadgeParser = (clipData: Video) => ({
  isRatedMature: clipData?.contentRating ? clipData.contentRating.some(isMature) : false,
  isAdvert: clipData?.contentRating ? clipData.contentRating.includes('advertisement') : false,
  is360Video: Boolean(clipData?.spatial?.stereoFormat),
  isDolbyVision: !!clipData?.embed?.badges?.dolbyVision,
  isHdr10: !!clipData?.embed?.badges?.hdr_10,
  isHdr10Plus: !!clipData?.embed?.badges?.hdr_10Plus,
  aiContent: clipData?.metadata?.aiContent,
});

/**
 * Showcase video call expects fields to be prefixed with "clip."
 */
export const mapToClipFields = (fields: VideoFields[]) => {
  return fields.map((field) => `clip.${field}`) as VideoFields[];
};

/**
 * Showcase video response returns fields under "clip" object.
 */
export const extractClipData = (data: Record<string, unknown>) => {
  const clipData = data?.clip;
  if (!clipData) return undefined;
  return clipData as Video;
};

/*
 * Returns the pathname from the current URL
 */
export const getPathname = () => {
  if (typeof window === 'undefined') {
    return '';
  }
  return window.location.pathname;
};

/**
 * Retrieves a property value from a configuration object.
 *
 * @param {object | unknown} config - The configuration object or any other value.
 *   If an object, it may optionally contain a `value` property.
 * @param {string} [propertyKey='value'] - The key of the property to retrieve from the object.
 *   Defaults to 'value' if not provided.
 * @returns {unknown} - The value of the specified property if it exists, or the original `config` value.
 */
export const getConfigProperty = (
  config:
    | {
        value: unknown;
      }
    | unknown,
  propertyKey: string = 'value',
): unknown => {
  if (
    config !== null &&
    typeof config === 'object' &&
    'value' in config &&
    !Array.isArray(config)
  ) {
    return (config as Record<string, unknown>)?.[propertyKey];
  }

  return config;
};

export const getVideoDetailsDisplayConfig = ({
  showLike,
  showDescription,
  showDateAdded,
  showTitle,
  showViews,
}: VideoDetailsDisplayConfigOptions) => ({
  showTitle: Boolean(showTitle),
  showDescription: Boolean(showDescription),
  showCategories: true,
  showUploadedBy: false,
  showViews: Boolean(showViews),
  showCreativeCommons: true,
  showCredits: true,
  showDateAdded: Boolean(showDateAdded),
  showTags: true,
  showManageButton: false,
  showLike: Boolean(showLike),
});

export const isArchivedOrNonLive = (clip: Video) =>
  (clip.live && clip.live?.status === 'done') || !clip.live;

/**
 * Returns true if isNewCustomizeControls is false.
 * If isNewCustomizeControls is true, returns the provided value.
 */
export const getShowcaseConfigValue = (isNewCustomizeControls: boolean, value: unknown): boolean =>
  isNewCustomizeControls ? Boolean(value) : true;

/**
 * Function to open the clip view with the appropriate query parameters
 * @param history - The history object from react-router-dom
 * @param isNewCustomizeControls - Flag to check if new customize controls are enabled
 * @param live - Live object from the clip, used to get event ID if applicable
 * @param url - Clip URL to extract clip ID
 */
export const openClipView = (
  history: History,
  isNewCustomizeControls: boolean,
  playerContainerRef: RefObject<HTMLDivElement>,
  showcaseId: number,
  live?: Clip['live'],
  url?: string,
) => {
  if (!url || !isNewCustomizeControls) return;

  const clipDataId = url?.split?.('/videos/')?.[1]?.split(':')?.[0] || '';
  const eventId = live && getEventIdFromClipLive(live);

  const query = eventId ? `event=${eventId}&video=${clipDataId}` : `video=${clipDataId}`;

  if (!query) return;
  const playbackPath = `${SHOWCASE_BASE_URL}${showcaseId}/playback`;
  history.replace({
    pathname: playbackPath,
    search: query,
  });
  playerContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

/**
 * Function to return total views string for a clip
 */
export const getViewsString = (totalPlays: number | null) =>
  typeof totalPlays === 'number'
    ? translate({
        singular: '{COUNT} view',
        plural: '{COUNT} views',
        count: totalPlays,
        replacements: {
          COUNT: humanize(totalPlays ?? 0),
        },
      })
    : null;

/**
 * Converts text alignment values to CSS flexbox alignment values
 * @param alignment - Text alignment value ('left', 'center', 'right')
 * @returns CSS flexbox alignment value
 */
export const getAlignmentValue = (alignment: string) => {
  switch (alignment) {
    case 'center':
      return ALIGNMENT_VALUES.center;
    case 'right':
      return ALIGNMENT_VALUES.right;
    case 'left':
    default:
      return ALIGNMENT_VALUES.left;
  }
};

/**
 * Function to get the appropriate clip thumbnails based on the effective grid size
 */
export const getClipThumbnails = (clip: Clip, effectiveGridSize: GridSize) => {
  const isGridSizeLarge = ['l', 'xl'].includes(effectiveGridSize);
  const thumbIdx = isGridSizeLarge ? 1 : 0;
  const fallbackThumbnailUrl = isGridSizeLarge ? FALLBACK_THUMBNAIL_URL_2X : FALLBACK_THUMBNAIL_URL;
  const thumbnail = clip.pictures?.sizes?.[thumbIdx]?.link || fallbackThumbnailUrl;
  const thumbnail2x = clip.pictures?.sizes?.[1]?.link
    ? `${clip.pictures.sizes[1].link} 2x`
    : FALLBACK_THUMBNAIL_URL_2X;

  return { thumbnail, thumbnail2x };
};

export const isCI = (): boolean =>
  typeof window !== 'undefined' &&
  (process.env.NODE_ENV !== 'production' || window.location.host.endsWith('.ci.vimeows.com'));

export const getIsActiveState = (pathname: string | undefined, type: string) => {
  if (!pathname) return false;

  const currentPath = pathname?.split('/').pop() || '';
  return currentPath === type;
};
